<template>
  <div class="multi-experiment" @mouseup="stopDrag">
    <!-- Header -->
    <div v-if="workflowName || actionName || projectName" class="experiment-header">
      <div class="experiment-header-content">
        <span v-if="workflowName"><strong>Workflow:</strong> {{ workflowName }}</span>
        <span v-if="actionName"><strong>Activity:</strong> {{ actionName }}</span>
        <span v-if="projectName"><strong>Project:</strong> {{ projectName }}</span>
      </div>
    </div>

    <!-- Controls -->
    <nav class="navbar">
      <div class="dropdown-group dropdown-group--count">
        <label for="experiment-count">Experiments:</label>
        <select id="experiment-count" v-model="experimentCount" class="form-control">
          <option v-for="n in 100" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>

      <div class="dropdown-group">
        <label for="project">Project:</label>
        <select
          id="project"
          v-model="selectedProject"
          class="form-control"
          :disabled="!!localActionId"
        >
          <option disabled value="">Select Project</option>
          <option v-for="project in projects" :key="project.id" :value="project.id">
            {{ project.name }}
          </option>
        </select>
      </div>

      <div v-if="showStructureElementDropdown" class="dropdown-group">
        <label for="structure-element">Structure element:</label>
        <select
          id="structure-element"
          v-model="selectedStructureElement"
          class="form-control"
        >
          <option disabled value="">Select Structure Element</option>
          <option v-for="target in structureTargets" :key="target.id" :value="target.id">
            {{ formatStructureTargetLabel(target) }}
          </option>
        </select>
      </div>

      <div class="dropdown-group">
        <label for="location">Location:</label>
        <select
          id="location"
          v-model="selectedLocation"
          class="form-control"
          :disabled="isLocationLockedToStructureTarget"
        >
          <option disabled value="">Select Location</option>
          <option v-for="location in locations" :key="location.id" :value="location.id">
            {{ location.name }}
          </option>
        </select>
      </div>

      <div class="dropdown-group">
        <label for="mdlist">Template:</label>
        <select id="mdlist" v-model="selectedMdlist" class="form-control">
          <option disabled value="">Select MDList</option>
          <option v-for="mdlist in experimentMdlists" :key="mdlist.id" :value="mdlist.id">
            {{ mdlist.name }}
          </option>
        </select>
      </div>

      <div
        v-for="(templateId, index) in additionalTemplateSelections"
        :key="`extra-template-${index}`"
        class="dropdown-group dropdown-group--template"
      >
        <label :for="`sub-mdlist-${index}`">Extra Template {{ index + 1 }}:</label>
        <div class="template-select-row">
          <select
            :id="`sub-mdlist-${index}`"
            :value="templateId"
            class="form-control"
            @change="updateAdditionalTemplate(index, $event.target.value)"
          >
            <option disabled value="">Select Sub MDList</option>
            <option v-for="sub in availableAdditionalTemplates(index)" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
          </select>
          <button type="button" class="template-remove-btn" @click="removeAdditionalTemplate(index)">
            Remove
          </button>
        </div>
      </div>

      <div class="dropdown-group dropdown-group--template-action">
        <label>&nbsp;</label>
        <button type="button" class="secondary-button secondary-button--inline" @click="addAdditionalTemplate">
          Add more templates
        </button>
      </div>
    </nav>

    <!-- Centered buttons -->
    <div class="action-buttons">
      <button @click="generateTable" class="main-button">Select</button>
      <button
        class="secondary-button"
        :disabled="!selectedProject || !selectedLocation"
        @click="triggerFileSelection"
      >
        Create from Files
      </button>
      <input
        ref="fileInput"
        type="file"
        multiple
        style="display: none"
        @change="handleCreateFromFiles"
      />
    </div>

    <!-- Table -->
    <div v-if="experiments.length" class="table-container">
      <div class="table-wrapper">
        <table class="experiment-table">
          <thead>
            <tr>
              <th>#</th>
              <th>ID</th>
              <th>Project</th>
              <th>Location</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th v-for="key in mdlistKeys" :key="key.key" class="key-column">
                <span class="tooltip-container">
                  <span class="tooltip-text">{{ key.key }}</span>
                  {{ key.key.length > 10 ? key.key.slice(0, 10) + '...' : key.key }}
                </span>
                <br />
                <small v-if="key.value_type">({{ key.value_type }})</small>
                <small v-if="key.unit" class="unit-label">({{ key.unit }})</small>
              </th>
              <th>Files</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(experiment, index) in experiments" :key="index">
              <td class="number-column">{{ index + 1 }}</td>
              <td class="id-column">
                <input type="text" v-model="experiment.name" class="form-control id-input" />
              </td>
              <td>{{ experiment.projectName }}</td>
              <td>{{ experiment.locationName }}</td>
              <td><input type="datetime-local" v-model="experiment.startDate" class="form-control" /></td>
              <td><input type="datetime-local" v-model="experiment.endDate" class="form-control" /></td>

              <td v-for="(keyObj, keyIndex) in mdlistKeys" :key="keyObj.key">
                <input
                  type="text"
                  v-model="experiment.values[keyIndex]"
                  class="form-control"
                  @mousedown="startDrag(index, keyIndex, experiment.values[keyIndex])"
                  @mousemove="handleDrag(index, keyIndex)"
                />
              </td>

              <td class="file-column">
                <div
                  class="file-dropzone"
                  @dragover.prevent
                  @drop.prevent="handleFileDrop($event, index)"
                >
                  <div v-if="experiment.files && experiment.files.length" class="file-info">
                    {{ experiment.files.length }} file(s)
                  </div>
                  <div v-else class="file-placeholder">
                    Drag files here or click to upload
                  </div>
                  <input
                    type="file"
                    multiple
                    class="file-input"
                    @change="handleFileSelect($event, index)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="submit-container">
        <button @click="submitExperiments">Submit</button>
      </div>
    </div>
  </div>
  <SeriesUploadSession
    :visible="seriesUploadSession.visible"
    :uploadData="seriesUploadSession.uploadData"
    :entraId="loggedInUser.entra_id"
    @complete="handleSeriesUploadComplete"
    @close="handleSeriesUploadClose"
  />
</template>

<script>
import { API_ENDPOINTS } from "@/services/config.js";
import { useToast } from "vue-toastification";
import { mapState } from "vuex";
import apiService from "@/services/apiService";
import { normalizeMetadataKeyCollection } from "@/utils/metadataTransforms";
import { coerceValue } from "@/utils/valueTypeCoercion";
import SeriesUploadSession from "@/components/SeriesUploadSession.vue";

export default {
  components: { SeriesUploadSession },
  data() {
    return {
      experimentCount: 1,
      selectedProject: null,
      selectedStructureElement: "",
      selectedLocation: null,
      selectedMdlist: "",
      additionalTemplateSelections: [],
      projects: [],
      locations: [],
      structureTargets: [],
      mdlists: [],
      mdlistKeys: [],
      experiments: [],
      localActionId: null,
      workflowName: null,
      actionName: null,
      projectName: null,
      isDragging: false,
      dragStartIndex: null,
      dragStartCol: null,
      dragValue: null,
      seriesUploadSession: {
        visible: false,
        uploadData: [],
      },
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
    selectedProjectStructureSetId() {
      const selectedProjectId = Number(this.selectedProject);
      const selectedProject = this.projects.find((project) => Number(project.id) === selectedProjectId);
      return selectedProject?.structure_set_id ?? null;
    },
    selectedStructureTarget() {
      const selectedId = Number(this.selectedStructureElement);
      return this.structureTargets.find((item) => Number(item.id) === selectedId) || null;
    },
    isLocationLockedToStructureTarget() {
      return this.selectedStructureTarget?.location_id != null;
    },
    showStructureElementDropdown() {
      return Boolean(this.selectedProject);
    },
    experimentMdlists() {
      return this.mdlists;
    },
    selectedMdlistIds() {
      return [this.selectedMdlist, ...this.additionalTemplateSelections].filter(Boolean);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.localActionId = this.$route.query.actionId
      ? Number(this.$route.query.actionId)
      : null;
    this.selectedProject = this.$route.query.projectId
      ? Number(this.$route.query.projectId)
      : null;
    this.workflowName = this.$route.query.workflowName || null;
    this.actionName = this.$route.query.actionName || null;
    this.projectName = this.$route.query.projectName || null;

    this.fetchProjects();
    this.fetchLocations();
    this.fetchMdlists();

    const routeStructureElementId = this.$route.query.project_structure_element_id || this.$route.query.structureElementId;
    if (routeStructureElementId) {
      this.selectedStructureElement = Number(routeStructureElementId);
    }
  },
  watch: {
    selectedProject: {
      immediate: false,
      async handler() {
        await this.onProjectChange();
      },
    },
    selectedStructureElement() {
      this.onStructureTargetChange();
    },
    selectedMdlist() {
      this.sanitizeAdditionalTemplates();
    },
  },
  methods: {
    async fetchProjects() {
      this.projects = await apiService.fetchResources(API_ENDPOINTS.projects, this.loggedInUser.entra_id);
      if (this.selectedProject) {
        await this.onProjectChange();
      }
    },
    async fetchLocations() {
      this.locations = await apiService.fetchResources(API_ENDPOINTS.locations);
    },
    async fetchMdlists() {
      this.mdlists = await apiService.get(
        API_ENDPOINTS.mdlists,
        { entity_type: "experiment" },
        this.loggedInUser?.entra_id
      );
    },
    async fetchMdlistKeys(md_list_id) {
      const response = await apiService.fetchResources(API_ENDPOINTS.defaultKeys(md_list_id));
      return normalizeMetadataKeyCollection(response);
    },
    addAdditionalTemplate() {
      this.additionalTemplateSelections.push("");
    },
    removeAdditionalTemplate(index) {
      this.additionalTemplateSelections.splice(index, 1);
    },
    updateAdditionalTemplate(index, value) {
      this.additionalTemplateSelections.splice(index, 1, value);
      this.sanitizeAdditionalTemplates(index);
    },
    availableAdditionalTemplates(currentIndex) {
      const selectedIds = new Set(
        [this.selectedMdlist, ...this.additionalTemplateSelections.filter((_, index) => index !== currentIndex)]
          .filter(Boolean)
          .map((id) => Number(id))
      );

      return this.experimentMdlists.filter((mdlist) => !selectedIds.has(Number(mdlist.id)));
    },
    sanitizeAdditionalTemplates(changedIndex = null) {
      const used = new Set();
      if (this.selectedMdlist) {
        used.add(Number(this.selectedMdlist));
      }

      this.additionalTemplateSelections = this.additionalTemplateSelections.map((value, index) => {
        if (!value) {
          return value;
        }

        const numericValue = Number(value);
        const isCurrentChange = changedIndex === index;
        if (used.has(numericValue) && !isCurrentChange) {
          return "";
        }

        if (used.has(numericValue) && isCurrentChange) {
          return "";
        }

        used.add(numericValue);
        return value;
      });
    },
    async onProjectChange() {
      if (!this.selectedProject) {
        this.structureTargets = [];
        this.selectedStructureElement = "";
        return;
      }

      const currentSelection = Number(this.selectedStructureElement);

      try {
        const response = await apiService.getProjectStructureExperimentTargets(
          this.selectedProject,
          this.loggedInUser.entra_id,
          this.selectedProjectStructureSetId
        );
        this.structureTargets = Array.isArray(response)
          ? response
          : response?.items || response?.data?.items || [];

        const hasSelection = this.structureTargets.some((item) => Number(item.id) === currentSelection);
        if (!hasSelection) {
          this.selectedStructureElement = "";
        }
      } catch (error) {
        console.error("Failed to fetch eligible experiment targets:", error);
        this.structureTargets = [];
        this.selectedStructureElement = "";
      }
    },
    onStructureTargetChange() {
      const selectedTarget = this.selectedStructureTarget;
      if (!selectedTarget?.location_id) {
        return;
      }

      this.selectedLocation = Number(selectedTarget.location_id);
    },
    formatStructureTargetLabel(target) {
      const locationLabel = target?.location_name || "No location";
      return `${target.name} — ${locationLabel}`;
    },

    /** Standard "Select" workflow **/
    generateTable() {
      if (!this.selectedProject || !this.selectedLocation) {
        this.toast.error("Please select both a Project and a Location.");
        return;
      }
      if (this.showStructureElementDropdown && !this.selectedStructureElement) {
        this.toast.error("Please select a Structure Element.");
        return;
      }
      this.buildExperiments(this.experimentCount);
    },

    /** Create from files **/
    triggerFileSelection() {
      this.$refs.fileInput.click();
    },
    async handleCreateFromFiles(event) {
      const files = Array.from(event.target.files);
      if (!files.length) return;


      files.forEach((f) => {
        f.cleanedName = f.name.replace(/\.[^/.]+$/, ""); 
      });

      await this.buildExperiments(files.length, files);
      event.target.value = "";
    },

    async buildExperiments(count, files = null) {
      const selectedMdlistIds = this.selectedMdlistIds;
      const keySets = await Promise.all(
        selectedMdlistIds.map((id) => this.fetchMdlistKeys(id))
      );
      const uniqueKeys = new Map();
      keySets.flat().forEach((key) => {
        if (key?.id != null) {
          uniqueKeys.set(Number(key.id), key);
        }
      });
      this.mdlistKeys = Array.from(uniqueKeys.values()).sort((left, right) => {
        const mdListDiff = Number(left.metadata_list_id || 0) - Number(right.metadata_list_id || 0);
        if (mdListDiff !== 0) {
          return mdListDiff;
        }
        return Number(left.sort_order || 0) - Number(right.sort_order || 0);
      });

      this.experiments = Array.from({ length: count }, (_, index) => {
        const file = files ? files[index] : null;
        return {
          id: index + 1,
          name: file ? file.cleanedName : "",
          projectName: this.projects.find((p) => p.id === this.selectedProject)?.name || "",
          locationName: this.locations.find((l) => l.id === this.selectedLocation)?.name || "",
          startDate: "",
          endDate: "",
          values: Array(this.mdlistKeys.length).fill(""),
          files: file ? [file] : [],
        };
      });
    },

    handleFileDrop(event, index) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      const validFiles = droppedFiles.filter((f) => f.size < 200 * 1024 * 1024);
      if (validFiles.length < droppedFiles.length) {
        this.toast.warning("Some files exceeded 200MB and were skipped.");
      }
      this.experiments[index].files.push(...validFiles);
    },
    handleFileSelect(event, index) {
      const selectedFiles = Array.from(event.target.files);
      const validFiles = selectedFiles.filter((f) => f.size < 200 * 1024 * 1024);
      if (validFiles.length < selectedFiles.length) {
        this.toast.warning("Some files exceeded 200MB and were skipped.");
      }
      this.experiments[index].files.push(...validFiles);
      event.target.value = "";
    },

    async submitExperiments() {
      if (!this.validateExperiments()) return;

      let experimentsPayload;
      try {
        experimentsPayload = this.prepareExperimentsPayloadWithMdlists();
      } catch (error) {
        this.toast.error(error.message || "Validation failed.");
        return;
      }

      try {
        const response = await apiService.createResource(
          API_ENDPOINTS.createSeries,
          {
            project_id: this.selectedProject,
            expires_in_seconds: 3600,
            experiments: experimentsPayload,
          },
          this.loggedInUser.entra_id
        );

        this.toast.success("Experiments created successfully!");

        const createdExperiments = response?.experiments || [];
        const uploadData = this.buildUploadData(createdExperiments);

        if (uploadData.length > 0) {
          // Show the upload modal; navigation happens when it completes
          this.seriesUploadSession = { visible: true, uploadData };
        } else {
          // No files to upload — navigate immediately
          this.experiments = [];
          this.navigateBackAfterSeriesCreation();
        }
      } catch (error) {
        console.error("Error submitting experiments:", error);
        this.toast.error(
          error?.message ? `Failed to submit experiments: ${error.message}` : "Failed to submit experiments."
        );
      }
    },

    /**
     * Build the uploadData array expected by SeriesUploadSession from the
     * createSeries API response, matching server-side upload descriptors
     * back to the local File objects held in this.experiments.
     */
    buildUploadData(createdExperiments) {
      const uploadData = [];

      for (const [index, createdExp] of createdExperiments.entries()) {
        const uploads = createdExp.uploads || [];
        if (!uploads.length) continue;

        const localExp =
          this.experiments[index] ||
          this.experiments.find((e) => e.name === createdExp.name);
        if (!localExp) continue;

        uploadData.push({
          experimentId: createdExp.id,
          experimentName: createdExp.name || localExp.name,
          uploads,
          localFiles: localExp.files || [],
        });
      }

      return uploadData;
    },

    handleSeriesUploadComplete() {
      this.seriesUploadSession.visible = false;
      this.experiments = [];
      this.navigateBackAfterSeriesCreation();
    },

    handleSeriesUploadClose() {
      this.seriesUploadSession.visible = false;
      this.experiments = [];
      this.navigateBackAfterSeriesCreation();
    },

    navigateBackAfterSeriesCreation() {
      const returnToName = this.$route.query.returnToName;
      const returnToParams = this.$route.query.returnToParams;
      const returnToQuery = this.$route.query.returnToQuery;

      if (returnToName && typeof returnToName === "string") {
        try {
          this.$router.push({
            name: returnToName,
            params: returnToParams ? JSON.parse(returnToParams) : {},
            query: returnToQuery ? JSON.parse(returnToQuery) : {},
          });
          return;
        } catch (error) {
          console.warn("Failed to restore CreateSeries origin route", error);
        }
      }

      this.$router.push({ name: "Experiments" });
    },


    validateExperiments() {
      for (const exp of this.experiments) {
        if (!exp.name.trim()) {
          this.toast.error("Each experiment must have an ID.");
          return false;
        }
      }
      return true;
    },

    prepareExperimentsPayloadWithMdlists() {
      const selectedMdlistIds = this.selectedMdlistIds;

      return this.experiments.map((exp) => {
        const mdlists = selectedMdlistIds.map((mdlistId) => {
          const mdValues = [];
          exp.values.forEach((value, index) => {
            // Only include values for keys belonging to this mdlist
            const key = this.mdlistKeys[index];
            if (Number(key.metadata_list_id) === Number(mdlistId)) {
              let finalValue = value.trim() || null;

              // Validate and coerce value if provided
              if (finalValue !== null && key.value_type) {
                const coerceResult = coerceValue(finalValue, key.value_type);
                if (!coerceResult.valid) {
                  throw new Error(`Value validation failed for key "${key.name}": ${coerceResult.error}`);
                }
                finalValue = coerceResult.coercedValue;
              }

              mdValues.push({
                metadata_key_id: key.id,
                value: finalValue,
              });
            }
          });
          return {
            metadata_list_id: Number(mdlistId),
            metadata_values: mdValues,
          };
        });

        return {
          name: exp.name,
          start_date: exp.startDate
            ? new Date(exp.startDate).toISOString()
            : null,
          end_date: exp.endDate ? new Date(exp.endDate).toISOString() : null,
          action_id: this.localActionId ? Number(this.localActionId) : null,
          location_id: this.selectedLocation,
          project_id: this.selectedProject,
          project_structure_element_id: this.selectedStructureElement ? Number(this.selectedStructureElement) : null,
          metadata_lists: mdlists,
          files: (exp.files || []).map((file) => ({
            relative_path: this.getFileRelativePath(file),
          })),
        };
      });
    },

    getFileRelativePath(file) {
      return file.webkitRelativePath && file.webkitRelativePath.length > 0
        ? file.webkitRelativePath
        : file.name;
    },

    startDrag(rowIndex, colIndex, value) {
      this.isDragging = true;
      this.dragStartIndex = rowIndex;
      this.dragStartCol = colIndex;
      this.dragValue = value;
    },
    handleDrag(rowIndex, colIndex) {
      if (!this.isDragging) return;
      if (colIndex === this.dragStartCol && rowIndex > this.dragStartIndex) {
        for (let i = this.dragStartIndex + 1; i <= rowIndex; i++) {
          this.experiments[i].values[colIndex] = this.dragValue;
        }
      }
    },
    stopDrag() {
      this.isDragging = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

/* Keep your original styles */
.experiment-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
}
.experiment-header-content {
  display: flex;
  gap: 15px;
  font-size: 22px;
  font-weight: 500;
  color: #333;
}
.navbar {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding: 15px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  align-items: center;
}

.dropdown-group {
  display: grid;
  gap: 6px;
  flex: 1 1 200px;
  min-width: 170px;
  max-width: 240px;
}

.dropdown-group--count {
  flex: 0 0 110px;
  min-width: 90px;
  max-width: 110px;
}

.dropdown-group--count .form-control {
  max-width: 90px;
}

.dropdown-group--template {
  min-width: 260px;
  max-width: 360px;
}

.dropdown-group--template-action {
  flex: 0 0 auto;
  min-width: auto;
  max-width: none;
}

.dropdown-group label {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
}

.form-control {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  height: 36px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-group .form-control {
  padding-right: 28px;
}

.template-select-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.template-remove-btn {
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: #fff;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  white-space: nowrap;
}

.secondary-button--inline {
  width: auto;
  white-space: nowrap;
}

select.form-control {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

select.form-control option {
  white-space: nowrap;
}
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 15px;
}
.main-button,
.secondary-button {
  padding: 8px 18px;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
  border: none;
}
.secondary-button:disabled {
  background-color: #b0b0b0;
  cursor: not-allowed;
}

/* Keep all your original table and dropzone styles */
.multi-experiment {
  max-width: 1400px;
  margin: 20px auto;
  padding: 40px;
  background: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
  overflow-x: auto;
}
.table-container {
  overflow-x: auto;
  width: 100%;
  padding-bottom: 20px;
  text-align: center;
  margin-top: 20px;
}
.table-wrapper {
  display: block;
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;
}
.experiment-table {
  width: max-content;
  border-collapse: collapse;
  margin: auto;
}
.experiment-table th,
.experiment-table td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.experiment-table th {
  background: $secondary-color;
}
.key-column {
  min-width: 100px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
}
.id-column {
  min-width: 150px !important;
  text-align: center;
}
.id-column input {
  width: 100%;
  max-width: 240px;
  border: 1px solid #ccc;
  padding: 5px;
  text-align: center;
}
.number-column {
  min-width: 10px !important;
  text-align: center;
  font-weight: bold;
}
.tooltip-container {
  position: relative;
  display: inline-block;
}
.tooltip-text {
  visibility: hidden;
  background-color: black;
  color: white;
  text-align: center;
  padding: 5px;
  border-radius: 4px;
  position: absolute;
  z-index: 1000;
  top: 50%;
  left: auto;
  right: -10px;
  transform: translateY(-50%);
  white-space: nowrap;
}
.tooltip-container:hover .tooltip-text {
  visibility: visible;
}
.submit-container {
  text-align: center;
  margin-top: 10px;
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;
}

/* File dropzone styles */
.file-column {
  text-align: center;
  min-width: 180px;
}
.file-dropzone {
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  background: #f9f9f9;
}
.file-dropzone:hover {
  background-color: #f1f1f1;
}
.file-input {
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.file-info {
  font-weight: 500;
  color: #333;
}
.file-placeholder {
  font-size: 12px;
  color: #777;
}

</style>
